<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">Bank transactions</h1>
            <p class="text-muted mb-0">
              Money in and out of your bank accounts, matched to accounts and documents
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end flex-wrap mt-3 mt-md-0">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton
              color="primary"
              variant="outline"
              class="mr-2"
              :disabled="loadingStatements"
              @click="importFromBankStatement()"
            >
              <CIcon name="cil-cloud-upload" /> Import from statement
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add transaction
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardBody>
        <CInput
          v-if="items.length"
          v-model="search"
          placeholder="Search description, name or account"
          aria-label="Search transactions"
          class="mb-3"
        >
          <template #prepend-content><CIcon name="cil-magnifying-glass" /></template>
        </CInput>

        <div v-if="loading && !items.length" class="text-center p-5">
          <CSpinner color="primary" />
          <div class="text-muted mt-2">Loading transactions…</div>
        </div>

        <div v-else-if="error && !items.length" class="text-center p-5">
          <CIcon name="cil-warning" size="xl" class="text-danger mb-2" />
          <h2 class="h6 font-weight-bold">Couldn't load transactions</h2>
          <p class="text-muted">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">
            <CIcon name="cil-reload" /> Retry
          </CButton>
        </div>

        <div v-else-if="!items.length" class="text-center p-5">
          <CIcon name="cil-swap-horizontal" size="xl" class="text-muted mb-2" />
          <h2 class="h6 font-weight-bold">No transactions yet</h2>
          <p class="text-muted">
            Import them from an uploaded bank statement, or add one yourself.
          </p>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Add transaction
          </CButton>
        </div>

        <div v-else-if="!filteredItems.length" class="text-center p-4">
          <h2 class="h6 font-weight-bold">No transactions match your search</h2>
          <CButton color="light" @click="search = ''">
            <CIcon name="cil-x" /> Clear search
          </CButton>
        </div>

        <template v-else>
          <!-- Desktop table -->
          <div class="d-none d-lg-block">
            <CDataTable
              :items="filteredItems"
              :fields="fields"
              items-per-page-select
              :items-per-page="25"
              hover
              sorter
              pagination
              :loading="loading"
            >
              <template #amount="{ item }">
                <td class="text-right text-nowrap">
                  RM {{ formatMyr(item.amount) }}
                </td>
              </template>
              <template #show_details="{ item }">
                <td class="py-2 text-right text-nowrap">
                  <CButton
                    color="light"
                    size="sm"
                    class="mr-1"
                    :aria-label="`Edit transaction ${item.description || ''}`"
                    @click="onEdit(item)"
                  >
                    <CIcon name="cil-pencil" />
                  </CButton>
                  <CButton
                    color="light"
                    size="sm"
                    :aria-label="`Delete transaction ${item.description || ''}`"
                    @click="showDeleteConfirmation(item)"
                  >
                    <CIcon name="cil-trash" class="text-danger" />
                  </CButton>
                </td>
              </template>
            </CDataTable>
          </div>

          <!-- Mobile card list -->
          <div class="d-lg-none">
            <div
              v-for="item in filteredItems"
              :key="item.id"
              class="border rounded p-3 mb-2"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <div class="font-weight-bold">{{ item.description || "Transaction" }}</div>
                  <div class="small text-muted">
                    {{ item.dateDisplay }}
                    <span v-if="item.transactionTypeDescription">
                      · {{ item.transactionTypeDescription }}
                    </span>
                  </div>
                  <div v-if="item.categoryDisplay" class="small text-muted">
                    {{ item.categoryDisplay }}
                  </div>
                </div>
                <div class="font-weight-bold text-nowrap">
                  RM {{ formatMyr(item.amount) }}
                </div>
              </div>
              <div class="d-flex justify-content-end mt-2">
                <CButton
                  color="light"
                  class="mr-1"
                  :aria-label="`Edit transaction ${item.description || ''}`"
                  @click="onEdit(item)"
                >
                  <CIcon name="cil-pencil" />
                </CButton>
                <CButton
                  color="light"
                  :aria-label="`Delete transaction ${item.description || ''}`"
                  @click="showDeleteConfirmation(item)"
                >
                  <CIcon name="cil-trash" class="text-danger" />
                </CButton>
              </div>
            </div>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <!-- Delete confirm -->
    <CModal :show.sync="warningModal" :close-on-backdrop="!deleting" centered>
      <template #header>
        <h5 class="modal-title">Delete this transaction?</h5>
      </template>
      <p class="mb-0">
        “{{ itemToDelete.description || "Transaction" }}” on
        {{ itemToDelete.dateDisplay }} for RM {{ formatMyr(itemToDelete.amount) }}
        will be removed. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="deleting" @click="doDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" />Delete transaction
        </CButton>
      </template>
    </CModal>

    <!-- Choose a statement -->
    <CModal
      title="Choose a bank statement"
      :show.sync="importFromBankStatementPopup"
      size="lg"
      centered
    >
      <div v-if="!computedBankStatementList.length" class="text-center p-4 text-muted">
        No bank statements uploaded yet. Upload one from Bank accounts first.
      </div>
      <CDataTable
        v-else
        :items="computedBankStatementList"
        :fields="bankStatementFieldList"
        sorter
        pagination
        :items-per-page="10"
      >
        <template #show_details="{ item }">
          <td class="py-2">
            <CButton
              color="primary"
              variant="outline"
              size="sm"
              @click="onBankStatementSelected(item)"
            >
              Read statement
            </CButton>
          </td>
        </template>
      </CDataTable>
      <template #footer>
        <CButton color="light" @click="importFromBankStatementPopup = false">
          Cancel
        </CButton>
      </template>
    </CModal>

    <!-- Reading progress -->
    <CModal
      title="Reading bank statement"
      :show.sync="isExtractBankStatementLoading"
      :close-on-backdrop="false"
      centered
    >
      <p class="text-muted">This can take up to a minute.</p>
      <CProgress :value="progressValue" show-percentage animated />
      <template #header>
        <h5 class="modal-title">Reading bank statement</h5>
      </template>
      <template #footer><span></span></template>
    </CModal>

    <!-- Pick transactions to import -->
    <CModal
      title="Import bank transactions"
      size="lg"
      :show.sync="importBankTransactionPopup"
      :close-on-backdrop="false"
    >
      <div v-if="!computedBankTransctionItems.length" class="text-center p-4 text-muted">
        No transactions were found in this statement.
      </div>
      <template v-else>
        <p class="text-muted">
          Choose the transactions to add. {{ selectedBankTransactions.length }} of
          {{ computedBankTransctionItems.length }} selected.
        </p>
        <CDataTable
          :items="computedBankTransctionItems"
          :fields="bankTransactionFields"
          items-per-page-select
          :items-per-page="itemsPerPage"
          hover
          small
          pagination
          @page-change="onPageChange"
        >
          <template #show_select-header="">
            <CInputCheckbox
              :checked="isBankTransactionAllSelected"
              aria-label="Select all on this page"
              @change="toggleBankTranasctionSelectAll"
            />
          </template>

          <template #show_select="{ item }">
            <td class="py-2">
              <CInputCheckbox
                :checked="selectedBankTransactions.includes(item.id)"
                :aria-label="`Select ${item.description || 'transaction'}`"
                @change="toggleSelectionBankTransaction(item.id)"
              />
            </td>
          </template>
          <template #amount="{ item }">
            <td class="text-right text-nowrap">RM {{ formatMyr(item.amount) }}</td>
          </template>
        </CDataTable>
      </template>

      <template #footer>
        <div class="d-flex w-100 justify-content-between flex-wrap">
          <div>
            <CButton color="light" class="mr-2" @click="selectAllBankTranasctions">
              Select all
            </CButton>
            <CButton color="light" @click="deselectAllBankTranasctions">
              Clear selection
            </CButton>
          </div>
          <div>
            <CButton
              color="light"
              class="mr-2"
              :disabled="importing"
              @click="importBankTransactionPopup = false"
            >
              Cancel
            </CButton>
            <CButton
              color="primary"
              :disabled="importing || !selectedBankTransactions.length"
              @click="saveBankTransactions"
            >
              <CSpinner v-if="importing" size="sm" class="mr-1" />
              Import {{ selectedBankTransactions.length }}
              {{ selectedBankTransactions.length === 1 ? "transaction" : "transactions" }}
            </CButton>
          </div>
        </div>
      </template>
    </CModal>
  </div>
</template>

<script>
import TransactionApi from "@/lib/transactionApi";
import BankStatementApi from "../../lib/bankStatementApi";
import ResitAiApi from "@/lib/resitaiApi";
import { formatMyr } from "@/utils/money";
import { formatDate, mytToday } from "@/utils/datetime";

export default {
  name: "TransactionList",
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 10,
      selectedBankTransactions: [],
      bankTransactionItems: [],
      bankTransactionFields: [
        {
          key: "show_select",
          label: "",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
        { key: "dateDisplay", label: "Date" },
        { key: "description", label: "Description" },
        { key: "transactionTypeDescription", label: "Type" },
        { key: "amount", label: "Amount", _classes: "text-right" },
      ],
      importBankTransactionPopup: false,
      importing: false,

      isExtractBankStatementLoading: false,
      progressValue: 0,
      bankStatementApi: new BankStatementApi(),
      importFromBankStatementPopup: false,
      loadingStatements: false,
      bankStatementItems: [],
      selectedBankStatement: null,
      bankStatementFieldList: [
        { key: "bankName", label: "Bank" },
        { key: "dateDisplay", label: "Date" },
        {
          key: "show_details",
          label: "",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
      ],

      loading: true,
      error: false,
      deleting: false,
      search: "",
      items: [],
      fields: [
        { key: "dateDisplay", label: "Date" },
        { key: "description", label: "Description" },
        { key: "matchName", label: "Name" },
        { key: "transactionTypeDescription", label: "Type" },
        { key: "amount", label: "Amount", _classes: "text-right" },
        { key: "categoryDisplay", label: "Account" },
        { key: "transactionMatchDisplay", label: "Matched to" },
        { key: "remarks", label: "Remarks" },
        {
          key: "show_details",
          label: "",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
      ],
      api: new TransactionApi(),
      warningModal: false,
      itemToDelete: {},
    };
  },
  computed: {
    visibleBankTransctionItems() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.computedBankTransctionItems.slice(
        start,
        start + this.itemsPerPage
      );
    },
    isBankTransactionAllSelected() {
      return (
        this.visibleBankTransctionItems.length > 0 &&
        this.visibleBankTransctionItems.every((item) =>
          this.selectedBankTransactions.includes(item.id)
        )
      );
    },
    computedBankTransctionItems() {
      return this.bankTransactionItems.map((item) => {
        const date = this.getDate(item);
        return {
          ...item,
          date,
          dateDisplay: formatDate(date),
          amount: this.getAmount(item),
          description: item.transaction_description,
          transactionTypeDescription: this.getTransactoinTypeDescription(item),
          transactionType: this.getTransactoinType(item),
          transactionSourceType: 1,
          sourceBankStatementId: this.getSelectedBankStatementId(),
        };
      });
    },

    computedBankStatementList() {
      return this.bankStatementItems.map((item) => {
        return {
          ...item,
          bankName: item.bank ? item.bank.name : "",
          dateDisplay: formatDate(item.date),
        };
      });
    },
    computedItems() {
      return this.items.map((item) => {
        return {
          ...item,
          dateDisplay: formatDate(item.date),
          matchName: this.getMatchName(item),
          categoryDisplay: this.getChartAccountInfo(item),
          transactionMatchDisplay: this.getTransactionMatchDisplay(item),
          remarks: item.remarks ? item.remarks : "",
        };
      });
    },
    filteredItems() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.computedItems;
      return this.computedItems.filter((i) =>
        [i.description, i.matchName, i.categoryDisplay, i.remarks]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    },
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Accounting", "Bank transactions"]);
    this.refreshTable();
  },
  methods: {
    formatMyr,
    getSelectedBankStatementId() {
      if (this.selectedBankStatement) {
        return this.selectedBankStatement.id;
      }
      return null;
    },
    onPageChange(page) {
      this.currentPage = page;
    },

    toggleSelectionBankTransaction(id) {
      const index = this.selectedBankTransactions.indexOf(id);
      if (index === -1) {
        this.selectedBankTransactions.push(id);
      } else {
        this.selectedBankTransactions.splice(index, 1);
      }
    },
    deselectAllBankTranasctions() {
      this.selectedBankTransactions = [];
    },
    selectAllBankTranasctions() {
      this.selectedBankTransactions = []; // Reset first to force reactivity
      this.$nextTick(() => {
        this.selectedBankTransactions = this.computedBankTransctionItems.map(
          (item) => item.id
        );
      });
    },
    toggleBankTranasctionSelectAll() {
      const visibleIds = this.visibleBankTransctionItems.map((i) => i.id);
      if (this.isBankTransactionAllSelected) {
        this.selectedBankTransactions = this.selectedBankTransactions.filter(
          (id) => !visibleIds.includes(id)
        );
      } else {
        this.selectedBankTransactions = Array.from(
          new Set([...this.selectedBankTransactions, ...visibleIds])
        );
      }
    },
    /**
     * Statement dates arrive as "DD/MM" or "DD/MM/YY(YY)". Returns a wall-clock
     * "yyyy-MM-dd" string (no Date object, so no timezone drift).
     */
    getDate(item) {
      if (!item.entry_date) return null;

      const parts = item.entry_date.split("/");

      let day, month, year;

      if (parts.length === 2) {
        // Format: DD/MM — assume the current year in Malaysia
        [day, month] = parts;
        year = mytToday().substring(0, 4);
      } else if (parts.length === 3) {
        [day, month, year] = parts;
        year = year.length === 2 ? `20${year}` : year;
      } else {
        return null;
      }

      const d = parseInt(day, 10);
      const m = parseInt(month, 10);
      const y = parseInt(year, 10);
      if (!d || !m || !y || m > 12 || d > 31) return null;
      const pad = (n) => String(n).padStart(2, "0");
      return `${y}-${pad(m)}-${pad(d)}`;
    },
    getAmount(item) {
      if (!item.transaction_amount) return 0.0;
      return parseFloat(item.transaction_amount);
    },
    getTransactoinTypeDescription(item) {
      let amount = this.getAmount(item);
      if (!amount) return "Unknown";
      return parseFloat(amount) < 0 ? "Debit" : "Credit";
    },
    getTransactoinType(item) {
      let amount = this.getAmount(item);
      if (!amount) return 0;
      return parseFloat(amount) < 0 ? 1 : 0;
    },
    saveBankTransactions() {
      if (this.selectedBankTransactions.length === 0) return;

      const selectedTransactions = this.computedBankTransctionItems.filter(
        (event) => this.selectedBankTransactions.includes(event.id)
      );
      const count = selectedTransactions.length;

      this.importing = true;
      this.api
        .addBatch(selectedTransactions)
        .then(() => {
          this.refreshTable();
          this.$toast.success(
            `${count} ${count === 1 ? "transaction" : "transactions"} imported`
          );
          this.selectedBankTransactions = [];
          this.importBankTransactionPopup = false;
        })
        .catch(({ data } = {}) => {
          this.$toast.error(
            `Couldn't import the transactions. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          this.importing = false;
        });
    },
    onBankStatementSelected(item) {
      this.selectedBankStatement = item;
      var documentUrl = apiUrl + "documents/file/" + item.documentId;
      this.isExtractBankStatementLoading = true;

      this.extractImageFromUrl(documentUrl);
      this.importFromBankStatementPopup = false;
    },
    extractImageFromUrl(imageUrl) {
      this.progressValue = 0;
      this.bankTransactionItems = [];
      this.selectedBankTransactions = [];

      const interval = setInterval(() => {
        if (this.progressValue < 95) {
          this.progressValue += 5; // Simulated progress while the service works
        }
      }, 500);

      let succeeded = false;
      fetch(imageUrl)
        .then((response) => {
          return response.blob();
        })
        .then((imageBlob) => {
          const formData = new FormData();
          formData.append("file", imageBlob, "image.jpg");
          formData.append("document_type", "bank_statement");
          formData.append("bank_statement_name", "maybank");

          const resitAiApi = new ResitAiApi();
          return resitAiApi.extract(formData);
        })
        .then((result) => {
          this.progressValue = 100;
          this.bankTransactionItems = result.parsed_data.transactions || [];
          succeeded = true;
        })
        .catch((error) => {
          console.error("Error extracting data:", error);
          this.$toast.error(
            "Couldn't read this bank statement. Check that it's a clear PDF or image and try again."
          );
        })
        .finally(() => {
          clearInterval(interval);
          this.isExtractBankStatementLoading = false;
          if (succeeded) this.importBankTransactionPopup = true;
        });
    },

    importFromBankStatement() {
      this.selectedBankStatement = null;
      this.bankStatementItems = [];
      this.loadingStatements = true;
      this.bankStatementApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.bankStatementItems = response.result || [];
          this.importFromBankStatementPopup = true;
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error(
            "Couldn't load your bank statements. Check your connection and try again."
          );
        })
        .finally(() => {
          this.loadingStatements = false;
        });
    },
    getTransactionMatchDisplay(item) {
      if (item.transactionMatchType === 0 && item.invoice) return item.invoice.info;
      if (item.transactionMatchType === 1 && item.expense) return item.expense.info;
      return "";
    },
    getChartAccountInfo(item) {
      try {
        return `${item.chartAccount.accountNo} - ${
          item.chartAccount.category
        } - ${item.chartAccount.name.trim()}`;
      } catch (error) {
        return "";
      }
    },
    getMatchName(item) {
      try {
        if (item.transactionMatchType === 0) {
          return item.invoice.customer.name;
        } else if (item.transactionMatchType === 1) {
          return item.invoice.vendor.name;
        }
      } catch (error) {
        // Match record missing; show no name.
      }
      return "";
    },
    refreshTable() {
      this.loading = true;
      this.error = false;
      this.api
        .getListByCurrentBusiness()
        .then((response) => {
          this.items = response.result || [];
        })
        .catch((err) => {
          console.error(err);
          this.error = true;
          if (this.items.length) {
            this.$toast.error(
              "Couldn't refresh transactions. Check your connection and try again."
            );
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onEdit(item) {
      this.$router.push({ path: `/tenants/Transaction/${item.id}` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    doDelete() {
      this.deleting = true;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.$toast.success("Transaction deleted");
          this.warningModal = false;
          this.itemToDelete = {};
          this.refreshTable();
        })
        .catch(({ data } = {}) => {
          this.$toast.error(
            `Couldn't delete the transaction. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    addNew() {
      this.$router.push({ path: "/tenants/Transaction" });
    },
  },
};
</script>
